import { Immutable } from "immer";
import React from "react";
import { Key } from "ts-key-enum";
import { t } from "ttag";

import { Context } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { promoModalComponentsMap } from "../../blocks/componentMaps";

import styles from "./index.module.scss";

// localStorage flag that suppresses the auto-open after a visitor dismisses the
// modal. The exact key and value are load-bearing: they must match what
// already-dismissed visitors carry so their gate keeps working.
const VIEWED_STORAGE_KEY = "promo-modal";
const VIEWED_STORAGE_VALUE = "viewed";

type PromoModalAPI = Immutable<{
    isActive: boolean;
    // Open the modal; it only ever appears on the home page.
    open: () => void;
    // Overlay / close-button click: dismiss and record the viewed flag when
    // open; when closed, re-run the auto-open gate.
    toggle: () => void;
}>;

const PromoModalContext = React.createContext<PromoModalAPI | null>(null);

const usePromoModal = (): PromoModalAPI | null =>
    React.useContext(PromoModalContext);

export const PromoModalProvider = (props: {
    // Whether the `.promo-modal` markup is actually rendered on this page; the
    // auto-open / toggle gate is a no-op when it is not.
    present: boolean;
    children?: React.ReactNode;
}): React.JSX.Element => {
    const { present } = props;
    const [isActive, setIsActive] = React.useState(false);

    const open = React.useCallback(() => {
        // The modal only ever opens on the home page.
        if (getWindow()?.location.pathname !== "/") {
            return;
        }
        setIsActive(true);
    }, []);

    const close = React.useCallback(() => {
        setIsActive(false);
        getDocument()?.body.classList.remove("modal-open");
    }, []);

    const toggle = React.useCallback(() => {
        const win = getWindow();
        if (!win) {
            return;
        }
        if (isActive) {
            close();
            win.localStorage.setItem(VIEWED_STORAGE_KEY, VIEWED_STORAGE_VALUE);
        } else if (
            present &&
            win.localStorage.getItem(VIEWED_STORAGE_KEY) !==
                VIEWED_STORAGE_VALUE
        ) {
            open();
        }
    }, [isActive, present, close, open]);

    // Auto-open on the home page for visitors who have not dismissed it yet.
    // Client-only: the server cannot read localStorage, so it always emits the
    // inactive modal and the first client render matches before this effect may
    // activate it.
    React.useEffect(() => {
        const win = getWindow();
        if (
            present &&
            win &&
            win.location.pathname === "/" &&
            win.localStorage.getItem(VIEWED_STORAGE_KEY) !==
                VIEWED_STORAGE_VALUE
        ) {
            setIsActive(true);
        }
    }, [present]);

    // Escape closes the modal. Bind `keydown`, not `keypress`: keypress never
    // fires for Escape in modern browsers.
    React.useEffect(() => {
        const doc = getDocument();
        if (!doc) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === Key.Escape) {
                close();
            }
        };
        doc.addEventListener("keydown", onKeyDown);
        return () => {
            doc.removeEventListener("keydown", onKeyDown);
        };
    }, [close]);

    const value = React.useMemo<PromoModalAPI>(
        () => ({ isActive, open, toggle }),
        [isActive, open, toggle],
    );

    return (
        <PromoModalContext.Provider value={value}>
            {props.children}
        </PromoModalContext.Provider>
    );
};

const PromoModalBody = ({
    settings,
}: {
    settings: Immutable<
        React.ContextType<typeof Context>["chrome"]["promo_modal"]
    >;
}): React.JSX.Element | null => {
    if (!settings.body) {
        return null;
    }
    return (
        <>
            {settings.body.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={promoModalComponentsMap}
                />
            ))}
        </>
    );
};

export const PromoModal = (
    props: Immutable<{
        settings: React.ContextType<typeof Context>["chrome"]["promo_modal"];
        staticBase: string;
    }>,
): React.JSX.Element => {
    const promo = usePromoModal();
    const isActive = promo?.isActive ?? false;
    return (
        <div
            className={concatClassNames([
                styles.promoModal,
                isActive ? styles.promoModalActive : null,
            ])}
            // The whole overlay is the dismiss target: a click anywhere --
            // backdrop, container, or the close button that bubbles up to it --
            // toggles the modal shut. Keyboard users dismiss via the provider's
            // Escape handler.
            onClick={() => {
                promo?.toggle();
            }}
        >
            <div className={styles.promoModalBackdrop} />
            <div className={styles.promoModalContainer}>
                <PromoModalBody settings={props.settings} />
                <div className={styles.promoModalClose}>
                    <img
                        src={`${props.staticBase}img/global/close.svg`}
                        alt={t`Close Icon`}
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            </div>
        </div>
    );
};
